import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogoMark } from "@/components/app/Brand";
import HeroBackdrop from "@/components/app/HeroBackdrop";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/auth?prefill_reset=1"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0D1117]">
      <div className="hidden lg:block relative border-r border-[#1F2937] bg-[#060D1A] text-white overflow-hidden">
        <HeroBackdrop />
      </div>
      <div className="flex flex-col p-6 sm:p-10">
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <LogoMark />
            </div>

          {!token ? (
            <div data-testid="reset-no-token">
              <div className="flex items-center gap-2 text-rose-700 mb-3">
                <AlertTriangle className="w-5 h-5" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Missing reset token</h1>
              </div>
              <p className="text-sm text-slate-400">
                Open the link from your email again, or{" "}
                <Link to="/forgot-password" className="text-[#002FA7] font-semibold">request a new one</Link>.
              </p>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="w-5 h-5" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Password updated</h1>
              </div>
              <p className="text-sm text-slate-400">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] mb-2">Choose a new password</div>
              <h1 className="font-display font-black text-3xl text-white tracking-tighter mb-8">Set new password.</h1>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.2em] text-slate-400">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-none mt-1 h-11"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Confirm</Label>
                  <Input
                    id="confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-none mt-1 h-11"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button type="submit" disabled={busy} className="rounded-none w-full h-11 bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="reset-submit-btn">
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
